import { createTheme as createThemeV5 } from '@mui/material/styles';
import { createTheme as createThemeV4 } from '@material-ui/core/styles';
import { ruRU } from '@mui/x-data-grid';

// ── Design tokens (from landing) ──────────────────────────────
const tokens = {
    colors: {
        navy:    '#0B1426',
        accent:  '#3B82F6',
        green:   '#10B981',
        red:     '#EF4444',
        amber:   '#F59E0B',
        surface: '#F4F6FA',
        ice:     '#E8ECF2',
        slate:   '#64748B',
        white:   '#FFFFFF',
    },
    fonts: {
        heading: '"Space Grotesk", sans-serif',
        body:    '"DM Sans", sans-serif',
        mono:    '"JetBrains Mono", monospace',
    },
    radius: {
        sm: 8,
        md: 12,
        lg: 16,
    },
    shadows: {
        card: '0 1px 3px rgba(11,20,38,0.06), 0 1px 2px rgba(11,20,38,0.04)',
        cardHover: '0 10px 25px rgba(11,20,38,0.08)',
    },
};

// ── Shared palette / typography ───────────────────────────────
const palette = {
    primary:    { main: tokens.colors.navy, contrastText: tokens.colors.white },
    secondary:  { main: tokens.colors.accent },
    success:    { main: tokens.colors.green },
    error:      { main: tokens.colors.red },
    warning:    { main: tokens.colors.amber },
    background: { default: tokens.colors.surface, paper: tokens.colors.white },
    text:       { primary: tokens.colors.navy, secondary: tokens.colors.slate },
};

const typography = {
    fontFamily: tokens.fonts.body,
    h1: { fontFamily: tokens.fonts.heading },
    h2: { fontFamily: tokens.fonts.heading },
    h3: { fontFamily: tokens.fonts.heading },
    h4: { fontFamily: tokens.fonts.heading },
    h5: { fontFamily: tokens.fonts.heading },
    h6: { fontFamily: tokens.fonts.heading },
    subtitle1: { fontFamily: tokens.fonts.heading },
    subtitle2: { fontFamily: tokens.fonts.heading },
};

const shape = { borderRadius: tokens.radius.md };

// ── MUI v5 theme ─────────────────────────────────────────────
export const themeV5 = createThemeV5({
    palette,
    typography,
    shape,
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: tokens.colors.surface,
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: tokens.radius.md,
                    textTransform: 'none',
                    fontFamily: tokens.fonts.heading,
                    fontWeight: 500,
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: tokens.radius.lg,
                    border: `1px solid ${tokens.colors.ice}`,
                    boxShadow: tokens.shadows.card,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                rounded: {
                    borderRadius: tokens.radius.lg,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: tokens.radius.lg,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontFamily: tokens.fonts.heading,
                    textTransform: 'none',
                },
            },
        },
    },
}, ruRU);

// ── MUI v4 theme ─────────────────────────────────────────────
export const themeV4 = createThemeV4({
    palette,
    typography,
    shape,
    overrides: {
        MuiTabs: {
            indicator: { backgroundColor: tokens.colors.green },
        },
        MuiButton: {
            root: {
                borderRadius: tokens.radius.md,
                textTransform: 'none',
                fontFamily: tokens.fonts.heading,
                fontWeight: 500,
            },
            contained: {
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
            },
        },
        MuiCard: {
            root: {
                borderRadius: tokens.radius.lg,
                border: `1px solid ${tokens.colors.ice}`,
                boxShadow: tokens.shadows.card,
            },
        },
        MuiPaper: {
            rounded: {
                borderRadius: tokens.radius.lg,
            },
        },
        MuiDialog: {
            paper: {
                borderRadius: tokens.radius.lg,
            },
        },
        MuiTab: {
            root: {
                fontFamily: tokens.fonts.heading,
                textTransform: 'none',
            },
        },
        MuiMenu: {
            paper: {
                border: `1px solid ${tokens.colors.ice}`,
                boxShadow: tokens.shadows.cardHover,
            },
        },
        MuiTableRow: {
            root: {
                '&:hover': {
                    backgroundColor: tokens.colors.surface,
                },
            },
            hover: {
                '&:hover': {
                    backgroundColor: `${tokens.colors.surface} !important`,
                },
            },
        },
        MuiBottomNavigation: {
            root: {
                backgroundColor: tokens.colors.navy,
            },
        },
    },
});

export { tokens };
